import { useState, useEffect } from 'react';

/**
 * Custom hook for managing quiz state
 * Handles current question, answers, navigation, and session timing
 */
export function useQuizState() {
  // Quiz flow stages
  const [stage, setStage] = useState('landing'); // 'landing' | 'question' | 'capture' | 'results'
  
  // Current question index (0-based)
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  
  // All answers stored as { questionId: answerId }
  const [answers, setAnswers] = useState({});
  
  // Lead capture form data
  const [leadData, setLeadData] = useState({
    fullName: '',
    email: '',
    companyName: '',
    companyWebsite: ''
  });
  
  // Session timing for analytics
  const [sessionStartTime, setSessionStartTime] = useState(null);
  const [sessionCompleteTime, setSessionCompleteTime] = useState(null);
  
  // Start session timer when quiz begins
  useEffect(() => {
    if (stage === 'question' && !sessionStartTime) {
      setSessionStartTime(new Date().toISOString());
    }
  }, [stage, sessionStartTime]);
  
  // Calculate time to complete in seconds
  const getTimeToComplete = () => {
    if (!sessionStartTime || !sessionCompleteTime) return 0;
    const start = new Date(sessionStartTime);
    const end = new Date(sessionCompleteTime);
    return Math.round((end - start) / 1000);
  };
  
  // Start quiz (move from landing to first question)
  const startQuiz = () => {
    setStage('question');
    setCurrentQuestionIndex(0);
  };
  
  // Answer a question
  const answerQuestion = (questionId, answerId) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: answerId
    }));
  };
  
  // Navigate to next question or capture form
  const nextQuestion = (totalQuestions) => {
    if (currentQuestionIndex < totalQuestions - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    } else {
      // All questions answered, go to lead capture
      setStage('capture');
    }
  };
  
  // Navigate to previous question
  const previousQuestion = () => {
    // If on capture stage, go back to last question
    if (stage === 'capture') {
      setStage('question');
      return;
    }
    
    // If on questions, go to previous question or landing
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    } else {
      // Back to landing
      setStage('landing');
    }
  };
  
  // Submit lead capture form and show results
  const submitLeadCapture = (formData) => {
    setLeadData(formData);
    setSessionCompleteTime(new Date().toISOString());
    setStage('results');
  };
  
  // Calculate progress percentage
  const getProgress = (totalQuestions) => {
    if (stage === 'landing') return 0;
    if (stage === 'capture') return 95;
    if (stage === 'results') return 100;
    return Math.round(((currentQuestionIndex + 1) / totalQuestions) * 100);
  };
  
  // Check if current question is answered
  const isCurrentQuestionAnswered = (questionId) => {
    return answers[questionId] !== undefined;
  };
  
  // Reset quiz to initial state
  const resetQuiz = () => {
    setStage('landing');
    setCurrentQuestionIndex(0);
    setAnswers({});
    setLeadData({
      fullName: '',
      email: '',
      companyName: '',
      companyWebsite: ''
    });
    setSessionStartTime(null);
    setSessionCompleteTime(null);
  };
  
  return {
    // State
    stage,
    currentQuestionIndex,
    answers,
    leadData,
    sessionStartTime,
    sessionCompleteTime,
    
    // Actions
    startQuiz,
    answerQuestion,
    nextQuestion,
    previousQuestion,
    submitLeadCapture,
    resetQuiz,
    
    // Computed
    getProgress,
    getTimeToComplete,
    isCurrentQuestionAnswered,
  };
}

